import {
  CdkDrag,
  CdkDragDrop,
  DropListOrientation,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { SnackBarService } from '../../../../shared/services/snack/snack-bar.service';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation.component';
import { Dashboard } from '../../../dashboards/models/dashboard.model';
import { TileCreatorService } from '../../../tile-creator/services/tile-creator.service';
import { IGridColumn } from '../../interfaces/grid-column.interface';
import { IGridRow } from '../../interfaces/grid-row.interface';
import { IGridSettings } from '../../interfaces/grid-settings.interfaces';
import { IGrid } from '../../interfaces/grid.interface';
import { TileJsonDialogComponent } from '../tile-json-dialog/tile-json-dialog.component';

@Component({
  standalone: false,
  selector: 'suvo-bi-smart-grid',
  templateUrl: './smart-grid.component.html',
  styleUrls: ['./smart-grid.component.scss'],
})
export class SmartGridComponent implements OnInit, OnDestroy {
  @Input() editMode: boolean;
  @Input() grid: IGrid | any;
  @Input() settings: IGridSettings;
  @Input() saveChangesAsync: (grid: IGrid, publish: boolean) => Promise<any>;
  @Input() isCreation = false;
  @Input() saveMode = 'draftsAndPublish';
  @Input() scrollEnabled = false;
  @Input() displayNameLabel: string;

  @Output() onCreate = new EventEmitter<Dashboard>();

  @Output() contentChanged = new EventEmitter<boolean>();

  @ContentChild(TemplateRef) tileOutlet: TemplateRef<any>;

  editingActive = false;

  changesUnsaved = false;

  sectionConnectedListIds: string[] = [];
  rowConnectedListIds: string[] = [];
  rowDropListOrientation: DropListOrientation = 'horizontal';

  gridForm: FormGroup;

  get displayNameControl(): AbstractControl {
    return this.gridForm.get('displayName') as AbstractControl;
  }

  get shortCodeControl(): AbstractControl {
    return this.gridForm.get('shortCode') as AbstractControl;
  }

  get canPublishWithoutChanges(): boolean {
    return !this.isCreation && !this.changesUnsaved && this.grid?.published === false;
  }

  get publishButtonLabel(): string {
    if (this.isCreation) {
      return 'Create';
    }

    return this.canPublishWithoutChanges ? 'Publish' : 'Save & Publish';
  }

  get publishButtonDisabled(): boolean {
    if (this.gridForm?.invalid) {
      return true;
    }

    if (this.isCreation) {
      return false;
    }

    return !this.changesUnsaved && this.grid?.published !== false;
  }

  private lastSavedGrid: IGrid;

  private readonly tileToolbarForegroundCache = new WeakMap<
    HTMLElement,
    { backgroundColor: string; foregroundColor: string }
  >();

  private activeDialogRef: MatDialogRef<TileJsonDialogComponent>;

  private unsubscribe$ = new Subject<boolean>();

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly snackBarService: SnackBarService,
    private readonly matDialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly tileCreatorService: TileCreatorService,
  ) {}

  ngOnInit() {
    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((result) => {
        this.rowDropListOrientation = result.matches ? 'vertical' : 'horizontal';
      });

    this.recreateConnectedListIds();
    this.setDeepCopySave();
    this.initForm();
    this.initOnChangedSubscription();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes) {
    if (changes.grid) {
      this.changesUnsaved = false;

      this.setDeepCopySave();
    }

    this.initForm();
  }

  /*  Edit Add/Remove Actions
   */

  addColumnToRow(sectionIndex: number, rowIndex: number, insertIndex?: number) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];
    const maxColumnCount = this.settings?.maxColumnCount ?? 3;

    if (row.columns.length < maxColumnCount) {
      const newColumn: IGridColumn = {
        tileDefinition: {
          tileType: 'new',
          canView: true,
        },
      };
      if (insertIndex === undefined || insertIndex < 0 || insertIndex > row.columns.length) {
        row.columns.push(newColumn);
      } else {
        row.columns.splice(insertIndex, 0, newColumn);
      }
    }

    this.onChanged(true);
  }

  addRow(sectionIndex: number, rowIndex?: number) {
    const section = this.grid.sections[sectionIndex];
    const newRow: IGridRow = {
      columns: [
        {
          tileDefinition: {
            tileType: 'new',
            canView: true,
          },
        },
      ],
    };

    if (rowIndex) {
      section.rows.splice(rowIndex, 0, newRow);
    } else {
      section.rows.push(newRow);
    }

    this.recreateConnectedListIds();
    this.onChanged(true);
  }

  removeRow(sectionIndex: number, rowIndex: number) {
    const section = this.grid.sections[sectionIndex];

    section.rows.splice(rowIndex, 1);

    this.recreateConnectedListIds();
    this.onChanged(true);
  }

  removeColumnFromRow(sectionIndex: number, rowIndex: number, columnIndex: number) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];

    row.columns.splice(columnIndex, 1);

    if (!row.columns.length) {
      this.removeRow(sectionIndex, rowIndex);
    }

    this.onChanged(true);
  }

  getRowColumnCountOptions(): number[] {
    return [1, 2, 3].filter((count) => count <= (this.settings?.maxColumnCount ?? 3));
  }

  async setRowColumnCount(sectionIndex: number, rowIndex: number, targetCount: number) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];
    const maxColumnCount = this.settings?.maxColumnCount ?? 3;
    const nextCount = Math.max(1, Math.min(targetCount, maxColumnCount));
    const currentCount = row.columns.length;

    if (nextCount === currentCount) {
      return;
    }

    if (nextCount > currentCount) {
      while (row.columns.length < nextCount) {
        row.columns.push({
          tileDefinition: {
            tileType: 'new',
            canView: true,
          },
        });
      }

      this.onChanged(true);
      return;
    }

    const columnsToRemove = row.columns.slice(nextCount);
    const tileCountToRemove = columnsToRemove.reduce(
      (count, column) => count + this.getColumnTileCount(column),
      0,
    );

    if (tileCountToRemove > 0) {
      const confirmationDialog = this.matDialog.open(ConfirmationDialogComponent, {
        data: {
          confirmationText:
            tileCountToRemove === 1
              ? 'Reducing this row will delete 1 tile. Continue?'
              : `Reducing this row will delete ${tileCountToRemove} tiles. Continue?`,
        },
      });

      const confirmed = await confirmationDialog.afterClosed().pipe(take(1)).toPromise();

      if (!confirmed) {
        return;
      }
    }

    row.columns.splice(nextCount);
    this.onChanged(true);
  }

  /*  Drag and Drop
   */

  sectionDropped = (event: CdkDragDrop<any[]>) =>
    this.dragDropRearrange(event) && this.onChanged(true);

  rowDropped = (event: CdkDragDrop<any[]>) => this.dragDropRearrange(event) && this.onChanged(true);

  tileDropped = (event: CdkDragDrop<any[]>) =>
    this.dragDropRearrange(event) && this.onChanged(true);

  tileEnterPredicate(dragItem: CdkDrag<IGridColumn>, dropList: any) {
    const maxColumnCount = this.settings?.maxColumnCount ?? 3;
    return dropList.data.length < maxColumnCount;
  }

  private recreateConnectedListIds() {
    //  Nested cdkLists don't work and so the rows are connected by a list of ids.
    //  These ids have to be reassigned when the number of rows changes.

    let sectionConnectedListIds = [];
    let rowConnectedListIds = [];
    let runningRows = 0;
    let runningSections = 0;

    this.grid?.sections?.forEach((section) => {
      sectionConnectedListIds.push(`Section${runningSections}`);
      runningSections += 1;

      section.rows.forEach((row) => {
        rowConnectedListIds.push(`Row${runningRows}`);
        runningRows += 1;
      });
    });

    this.sectionConnectedListIds = sectionConnectedListIds;
    this.rowConnectedListIds = rowConnectedListIds;
  }

  private dragDropRearrange(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      if (event.previousIndex == event.currentIndex) {
        return false; // No Changes.
      }
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    return true;
  }

  /*  Save and Changes
   */

  setDeepCopySave() {
    this.lastSavedGrid = JSON.parse(JSON.stringify(this.grid));
  }

  discardChanges() {
    this.grid = this.lastSavedGrid;
    this.setDeepCopySave();
    this.editingActive = false;
    this.onChanged(true);
  }

  async saveChanges(publish) {
    this.changesUnsaved = false;

    try {
      await this.saveChangesAsync(this.grid, publish);
      this.setDeepCopySave();
      this.snackBarService.open('Saved.');
    } catch (e) {
      this.snackBarService.open('Something went wrong. Unable to save.');
      throw e;
    } finally {
      this.onChanged(true);
    }
  }

  private onChanged(refreshGrid = false) {
    if (this.grid) {
      let stringifiedGrid = JSON.stringify(this.grid);

      this.changesUnsaved = stringifiedGrid !== JSON.stringify(this.lastSavedGrid);

      this.contentChanged.next(this.changesUnsaved);

      if (refreshGrid) {
        //Trigger change detection on grid - maybe find better solution - problems with getting changes to be noticed through template ref
        //this.applicationRef.tick();

        this.grid = JSON.parse(stringifiedGrid);
      }

      this.initForm();
    }
  }

  toggleColumnDisplayProperty(
    propertyName: string,
    sectionIndex: number,
    rowIndex: number,
    columnIndex: number,
  ) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];
    const column = row.columns[columnIndex];

    this.toggleDisplayProperty(propertyName, column.tileDefinition);
  }

  toggleDisplayProperty(propertyName: string, definition: any) {
    if (!definition.displayProperties) definition.displayProperties = {};

    definition.displayProperties[propertyName] = !definition.displayProperties?.[propertyName];

    this.onChanged(true);
  }

  toggleDashboardProperty(propertyName: string) {
    this.grid[propertyName] = !this.grid[propertyName];
    this.onChanged(true);
  }

  openColumnEditDialog(sectionIndex: number, rowIndex: number, columnIndex: number) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];
    const column = row.columns[columnIndex];

    if (!this.canEditTile(column.tileDefinition)) {
      return;
    }

    this.tileCreatorService.requestTileUpdate(column.tileDefinition);
  }

  canEditTile(tileDefinition: any): boolean {
    const tileType = tileDefinition?.tileType;
    return !!tileType && tileType !== 'new';
  }

  getTileToolbarForegroundColor(element: HTMLElement | null | undefined): string {
    if (!element || typeof window === 'undefined') {
      return '#000000';
    }

    const backgroundColor = this.getNearestBackgroundColor(element);
    const cached = this.tileToolbarForegroundCache.get(element);

    if (cached?.backgroundColor === backgroundColor) {
      return cached.foregroundColor;
    }

    const foregroundColor = this.getBestContrastTextColor(backgroundColor);

    this.tileToolbarForegroundCache.set(element, {
      backgroundColor,
      foregroundColor,
    });

    return foregroundColor;
  }

  private getColumnTileCount(column: IGridColumn): number {
    const tileDefinitionsCount = Array.isArray(column.tileDefinitions)
      ? column.tileDefinitions.filter((definition) => this.canEditTile(definition)).length
      : 0;

    if (tileDefinitionsCount > 0) {
      return tileDefinitionsCount;
    }

    return this.canEditTile(column.tileDefinition) ? 1 : 0;
  }

  private getNearestBackgroundColor(element: HTMLElement): string {
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
      const backgroundColor = window.getComputedStyle(currentElement).backgroundColor;

      if (!this.isTransparentColor(backgroundColor)) {
        return backgroundColor;
      }

      currentElement = currentElement.parentElement;
    }

    return 'rgb(255, 255, 255)';
  }

  private isTransparentColor(color: string): boolean {
    if (!color || color === 'transparent') {
      return true;
    }

    const channels = this.parseRgbColor(color);

    return !channels || channels.alpha <= 0.01;
  }

  private getBestContrastTextColor(color: string): '#000000' | '#ffffff' {
    const channels = this.parseRgbColor(color);

    if (!channels) {
      return '#000000';
    }

    const alpha = channels.alpha ?? 1;
    const red = Math.round(channels.red * alpha + 255 * (1 - alpha));
    const green = Math.round(channels.green * alpha + 255 * (1 - alpha));
    const blue = Math.round(channels.blue * alpha + 255 * (1 - alpha));

    const luminance = this.getRelativeLuminance(red, green, blue);
    const contrastWithBlack = (luminance + 0.05) / 0.05;
    const contrastWithWhite = 1.05 / (luminance + 0.05);

    return contrastWithBlack >= contrastWithWhite ? '#000000' : '#ffffff';
  }

  private getRelativeLuminance(red: number, green: number, blue: number): number {
    const toLinear = (channel: number) => {
      const value = channel / 255;

      return value <= 0.03928
        ? value / 12.92
        : Math.pow((value + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * toLinear(red) + 0.7152 * toLinear(green) + 0.0722 * toLinear(blue);
  }

  private parseRgbColor(
    color: string,
  ): { red: number; green: number; blue: number; alpha: number } | null {
    const match = color.match(/rgba?\(([^)]+)\)/i);

    if (!match) {
      return null;
    }

    const [red, green, blue, alpha = '1'] = match[1].split(',').map((value) => value.trim());

    return {
      red: Number(red),
      green: Number(green),
      blue: Number(blue),
      alpha: Number(alpha),
    };
  }

  /*  Json Editting
   */

  editColumnJson(sectionIndex: number, rowIndex: number, columnIndex: number) {
    const section = this.grid.sections[sectionIndex];
    const row = section.rows[rowIndex];
    const column = row.columns[columnIndex];
    const inputJson = JSON.stringify(column.tileDefinition, null, 4);
    const jsonDialog = this.createJsonDialog('Edit Tile Json', inputJson);

    jsonDialog
      .afterClosed()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          column.tileDefinition = JSON.parse(result);
          this.onChanged(true);
        }
      });
  }

  editJson() {
    const { sections } = this.grid;
    const inputJson = JSON.stringify(sections, null, 4);
    const jsonDialog = this.createJsonDialog('Edit Json', inputJson);

    jsonDialog
      .afterClosed()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.grid.sections = JSON.parse(result);
          this.onChanged(true);
        }
      });
  }

  private createJsonDialog(title: string, inputJson: string) {
    this.activeDialogRef?.close();

    const jsonDialog = this.matDialog.open(TileJsonDialogComponent, {
      width: '768px',
      height: '768px',
      data: { title, json: inputJson },
    });

    this.activeDialogRef = jsonDialog;

    return jsonDialog;
  }

  onCreateClick() {
    this.onCreate.emit(this.grid as any);
  }

  private initForm() {
    if (!this.gridForm) {
      this.gridForm = this.fb.group({
        displayName: [this.grid.displayName, [Validators.required]],
        shortCode: [this.grid.shortCode, [Validators.required]],
      });

      this.displayNameControl.valueChanges.pipe(debounceTime(250)).subscribe((displayName) => {
        if (this.grid.displayName !== displayName) {
          this.grid.displayName = displayName;
          this.grid.shortCode = this.slugify(displayName);
          this.onChanged(false);
        }
      });

      this.shortCodeControl.valueChanges.pipe(debounceTime(250)).subscribe((shortCode) => {
        if (this.grid.shortCode !== shortCode) {
          this.grid.shortCode = shortCode;
          this.onChanged(false);
        }
      });
    } else {
      this.gridForm.controls.displayName.setValue(this.grid.displayName);
      this.gridForm.controls.shortCode.setValue(this.grid.shortCode);
    }
  }

  slugify = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  private initOnChangedSubscription() {
    this.tileCreatorService.onChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.onChanged(true);
    });
  }

  getInvalidFields() {
    let invalids = [];
    Object.keys(this.gridForm.controls).forEach((field) => {
      const control = this.gridForm.get(field);
      if (control.invalid) {
        invalids.push(`${field} is invalid`);
      }
    });

    return invalids;
  }

  startEditing() {
    this.prepareSections();

    this.editingActive = true;
  }

  stopEditing() {
    this.editingActive = false;
  }

  prepareSections() {
    if (!this.grid.sections) {
      this.grid.sections = [];
    }

    if (this.grid.sections.length == 0) {
      this.grid.sections.push({ rows: [] });
    }
  }
}
